const LOGO_ROTATION_STORAGE_KEY = 'iva_logo_visual_rotation_v1';
const LOGO_FIRST_OSCI_STORAGE_KEY = 'iva_logo_first_osci_pages_v1';
const DEFAULT_RASTER_VISUAL_SIZE = { width: 540, height: 540 };
const RASTER_VISUAL_SIZES = {
  'logo_shark.gif': { width: 720, height: 405 },
};

function loadRasterVisuals() {
  try {
    const req = require.context('./logo_art', false, /^\.\/logo_.*\.(gif|png|jpe?g|webp|avif)$/i);

    return req
      .keys()
      .sort()
      .map((key) => {
        const asset = req(key);
        const src = typeof asset === 'string' ? asset : asset?.default;
        const filename = key.replace(/^\.\//, '');

        if (!src) {
          return null;
        }

        const size = RASTER_VISUAL_SIZES[filename] || DEFAULT_RASTER_VISUAL_SIZE;

        return {
          id: `image:${filename}`,
          type: 'image',
          src,
          filename,
          width: size.width,
          height: size.height,
          aspectRatio: `${size.width} / ${size.height}`,
        };
      })
      .filter(Boolean);
  } catch (error) {
    console.error('Failed to load raster logo visuals', error);
    return [];
  }
}

function loadOsciAssetUrls() {
  try {
    const req = require.context('./svg for oscii', false, /\.svg$/i);

    return req
      .keys()
      .sort()
      .map((key) => {
        const asset = req(key);
        return typeof asset === 'string' ? asset : asset?.default;
      })
      .filter(Boolean);
  } catch (error) {
    console.error('Failed to load osci SVG assets', error);
    return [];
  }
}

function shuffleArray(items) {
  const copy = items.slice();

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }

  return copy;
}

function buildNextQueue(visualIds, lastServedId) {
  if (visualIds.length <= 1) {
    return visualIds.slice();
  }

  const queue = shuffleArray(visualIds);
  if (!lastServedId || queue[0] !== lastServedId) {
    return queue;
  }

  const swapIndex = queue.findIndex((id) => id !== lastServedId);
  if (swapIndex > 0) {
    [queue[0], queue[swapIndex]] = [queue[swapIndex], queue[0]];
  }

  return queue;
}

function loadRotationState(signature) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(LOGO_ROTATION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.signature !== signature || !Array.isArray(parsed.queue)) {
      return null;
    }

    return {
      queue: parsed.queue,
      cursor: Number.isInteger(parsed.cursor) ? parsed.cursor : 0,
      lastServedId: typeof parsed.lastServedId === 'string' ? parsed.lastServedId : null,
    };
  } catch (error) {
    console.error('Failed to load logo rotation state', error);
    return null;
  }
}

function saveRotationState(signature, state) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(
      LOGO_ROTATION_STORAGE_KEY,
      JSON.stringify({
        signature,
        queue: state.queue,
        cursor: state.cursor,
        lastServedId: state.lastServedId,
      })
    );
  } catch (error) {
    console.error('Failed to save logo rotation state', error);
  }
}

function normalizeLogoPageKey(pageKey) {
  const raw = String(pageKey || '').trim();
  if (!raw) return '/';
  if (raw === '/') return '/';
  return raw.replace(/\/+$/g, '') || '/';
}

function loadFirstOsciState(signature) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(LOGO_FIRST_OSCI_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.signature !== signature || !parsed.pages || typeof parsed.pages !== 'object') {
      return null;
    }

    return parsed.pages;
  } catch (error) {
    console.error('Failed to load first osci logo state', error);
    return null;
  }
}

function saveFirstOsciState(signature, pages) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(
      LOGO_FIRST_OSCI_STORAGE_KEY,
      JSON.stringify({
        signature,
        pages,
      })
    );
  } catch (error) {
    console.error('Failed to save first osci logo state', error);
  }
}

export const OSCI_SVG_ASSET_URLS = loadOsciAssetUrls();

export const LOGO_SEQUENCE = [
  ...loadRasterVisuals(),
  ...(OSCI_SVG_ASSET_URLS.length ? [{ id: 'osci', type: 'osci' }] : []),
];

const LOGO_ID_TO_INDEX = new Map(LOGO_SEQUENCE.map((visual, index) => [visual.id, index]));
const LOGO_VISUAL_SIGNATURE = LOGO_SEQUENCE.map((visual) => visual.id).join('|');

export function getNextGlobalLogoVisualIndex() {
  if (!LOGO_SEQUENCE.length) {
    return 0;
  }

  const visualIds = LOGO_SEQUENCE.map((visual) => visual.id);
  const storedState = loadRotationState(LOGO_VISUAL_SIGNATURE);
  let queue = storedState?.queue?.filter((id) => LOGO_ID_TO_INDEX.has(id)) || [];
  let cursor = storedState?.cursor || 0;
  let lastServedId = storedState?.lastServedId || null;

  if (!queue.length || cursor >= queue.length) {
    queue = buildNextQueue(visualIds, lastServedId);
    cursor = 0;
  }

  const nextId = queue[cursor] || visualIds[0];
  lastServedId = nextId;
  cursor += 1;

  saveRotationState(LOGO_VISUAL_SIGNATURE, {
    queue,
    cursor,
    lastServedId,
  });

  return LOGO_ID_TO_INDEX.get(nextId) ?? 0;
}

export function getInitialLogoVisualIndexForPage(pageKey) {
  const osciIndex = LOGO_ID_TO_INDEX.get('osci');
  if (!LOGO_SEQUENCE.length || osciIndex == null) {
    return getNextGlobalLogoVisualIndex();
  }

  const key = normalizeLogoPageKey(pageKey);
  const pages = loadFirstOsciState(LOGO_VISUAL_SIGNATURE) || {};
  if (pages[key]) {
    return getNextGlobalLogoVisualIndex();
  }

  saveFirstOsciState(LOGO_VISUAL_SIGNATURE, {
    ...pages,
    [key]: true,
  });
  return osciIndex;
}
